import isEqual from 'lodash/isEqual';
import {createCheckboxes, createDropdown, createInputField, createTextareaField, disableFormControls} from '../forms/formFields';
import {cloneValue} from '../forms/formState';

const configurations = {
    JsonDocumentReader: {text: ['pointer'], lists: ['keysToUse']},
    TextDocumentReader: {text: ['charset']},
    HtmlDocumentReader: {text: ['charset', 'selector', 'separator'], lists: ['metadataTags'],
        flags: ['allElements', 'groupByElement', 'includeLinkUrls']},
    MarkdownDocumentReader: {flags: ['horizontalRuleCreateDocument', 'includeCodeBlock', 'includeBlockquote']},
    YamlDocumentReader: {text: ['pointer'], lists: ['keysToUse']}
};

const helpResources = {
    JsonDocumentReader: ['documentReaderJSONHelpTitle', 'documentReaderJSONHelp', 'documentReaderSourceHelp',
        'documentReaderSelectionHelp', 'documentReaderResultHelp', 'documentReaderLimitsHelp'],
    TextDocumentReader: ['documentReaderTextHelpTitle', 'documentReaderTextHelp', 'documentReaderSourceHelp',
        'documentReaderTextOptionsHelp', 'documentReaderResultHelp', 'documentReaderLimitsHelp'],
    HtmlDocumentReader: ['documentReaderHTMLHelpTitle', 'documentReaderHTMLHelp', 'documentReaderSourceHelp',
        'documentReaderHTMLSelectionHelp', 'documentReaderHTMLMetadataHelp', 'documentReaderResultHelp', 'documentReaderLimitsHelp'],
    MarkdownDocumentReader: ['documentReaderMarkdownHelpTitle', 'documentReaderMarkdownHelp', 'documentReaderSourceHelp',
        'documentReaderMarkdownOptionsHelp', 'documentReaderMarkdownRecoveryHelp', 'documentReaderResultHelp', 'documentReaderLimitsHelp'],
    YamlDocumentReader: ['documentReaderYAMLHelpTitle', 'documentReaderYAMLHelp', 'documentReaderSourceHelp',
        'documentReaderSelectionHelp', 'documentReaderYAMLValuesHelp', 'documentReaderYAMLRecoveryHelp', 'documentReaderResultHelp', 'documentReaderLimitsHelp']
};

const createDocumentReaderTaskForm = ({task = {}, taskErrors = {}, editable = true,
                                         mxResources, onChange = () => {}} = {}) => {
    const configuration = configurations[task.type] ?? {};
    const readerFields = ['sourceExpression', ...Object.values(configuration).flat()];
    let current = Object.fromEntries(readerFields.filter(field => Object.hasOwn(task, field))
        .map(field => [field, cloneValue(task[field])]));
    let destroyed = false;
    let destroying = false;
    const element = document.createElement('div');
    element.id = 'documentReaderEditor';
    element.className = 'ui form';
    const controls = [];
    const optionControls = [];

    const sync = () => {
        if (!editable || destroyed) {
            return;
        }
        const next = cloneValue(current);
        controls.forEach(({input, field, fallback}) => {
            if (input.value !== String(current[field] ?? fallback)) {
                next[field] = input.value;
            }
        });
        optionControls.forEach(({input, field, multiple, boolean}) => {
            const value = multiple ? [...input.querySelectorAll('option')]
                .filter(option => option.selected).map(option => option.value)
                : boolean ? Boolean(input.checked) : input.value;
            const previous = current[field];
            const fallback = multiple ? (field === 'metadataTags' ? ['description', 'keywords'] : [])
                : boolean ? false : field === 'separator' ? '\n' : '';
            if (isEqual(value, previous ?? fallback)) {
                return;
            }
            if (typeof value === 'undefined' || value === '' && field !== 'separator') {
                delete next[field];
            } else {
                next[field] = value;
            }
        });
        if (!isEqual(current, next)) {
            current = next;
            onChange(cloneValue(current));
        }
    };

    const sourceField = createInputField({label: mxResources.get('documentReaderSourceExpression'),
        id: 'documentReaderSourceExpression', name: 'sourceExpression', type: 'text', className: 'required',
        placeHolder: mxResources.get('expressionPlaceholder'), value: current.sourceExpression ?? '',
        error: taskErrors?.fields?.sourceExpression});
    const sourceInput = sourceField.querySelector('input');
    controls.push({input: sourceInput, field: 'sourceExpression', fallback: ''});
    element.appendChild(sourceField);

    const configurationElement = document.createElement('div');
    element.appendChild(configurationElement);
    const label = field => mxResources.get(`documentReader${field[0].toUpperCase()}${field.slice(1)}`);
    const error = field => taskErrors?.fields?.[field];
    (configuration.text ?? []).forEach(field => {
        const createField = field === 'separator' ? createTextareaField : createInputField;
        const option = createField({label: label(field),
            id: `documentReader${field[0].toUpperCase()}${field.slice(1)}`, name: field,
            type: 'text', rows: 2,
            value: current[field] ?? (field === 'separator' ? '\n' : ''), error: error(field),
            placeHolder: field === 'charset' ? 'UTF-8' : undefined});
        optionControls.push({input: option.querySelector(field === 'separator' ? 'textarea' : 'input'), field});
        configurationElement.appendChild(option);
    });
    (configuration.lists ?? []).forEach(field => {
        const values = current[field] ?? (field === 'metadataTags' ? ['description', 'keywords'] : []);
        const option = createDropdown({label: label(field),
            id: `documentReader${field[0].toUpperCase()}${field.slice(1)}`, name: field,
            multiple: true, searchable: true, allowAdditions: editable, error: error(field),
            options: values.map(value => ({value, name: value}))});
        const input = option.querySelector('select');
        input.multiple = true;
        option.querySelectorAll('option').forEach(item => { item.selected = values.includes(item.value); });
        optionControls.push({input, field, multiple: true});
        configurationElement.appendChild(option);
    });
    (configuration.flags ?? []).forEach(field => {
        const value = current[field];
        const option = createCheckboxes({name: field, type: 'checkbox',
            value: value === true, error: error(field),
            options: [{value: true, label: label(field), disabled: !editable}]});
        const input = option.querySelector('input');
        input.id = `documentReader${field[0].toUpperCase()}${field.slice(1)}`;
        option.querySelector('label').setAttribute('for', input.id);
        optionControls.push({input, field, boolean: true});
        configurationElement.appendChild(option);
    });
    optionControls.forEach(({input}) => input.addEventListener('change', sync));
    controls.forEach(({input}) => input.addEventListener('change', sync));

    const help = document.createElement('details');
    const [title, ...paragraphs] = helpResources[task.type] ?? helpResources.JsonDocumentReader;
    const summary = document.createElement('summary');
    summary.textContent = mxResources.get(title);
    help.appendChild(summary);
    paragraphs.forEach(key => {
        const paragraph = document.createElement('p');
        paragraph.textContent = mxResources.get(key);
        help.appendChild(paragraph);
    });
    element.appendChild(help);
    if (!editable) {
        disableFormControls(element);
    }

    return {
        element,
        getTask: () => cloneValue(current),
        sync,
        appendJsonExpressionHelp(helpText) {
            if (!destroyed && helpText && !element.querySelector('#documentReaderExpressionHelp')) {
                const wrapper = document.createElement('div');
                wrapper.id = 'documentReaderExpressionHelp';
                wrapper.insertAdjacentHTML('beforeend', helpText);
                element.appendChild(wrapper);
            }
        },
        destroy() {
            if (!destroyed && !destroying) {
                destroying = true;
                sync();
                destroyed = true;
            }
        }
    };
};

export {createDocumentReaderTaskForm};
